import React, { useMemo, useState } from 'react'
import '../opportunity-html.scss'
import { CMSOpportunityDetailsType, CMSSectionBase, OpportunityDetails } from '../../../lib/types'
import { CMSStrengths } from './cms-strengths'
import { CMSSectionShell } from './cms-section-shell'
import { CMS_SECTION_META, CMSSectionKey, getCMSSectionTitle } from './cms-section-config'
import { OpportunityExpandableSection } from './opportunity-expandable-section'
import { resolveCmsSectionBackgroundHex } from './cms-section-text-content'
import OpportunityEstimatedFinancials from './opportunity-detail-estimated-financials'
import OpportunityDetailNews from './opportunity-detail-news'
import OpportunityDetailLeadershipTeam from './opportunity-detail-leadership-team'
import {
  getSortedOpportunityDetailSanitySections,
  OpportunityDetailSanitySection,
} from '../opportunity-detail.utils'
import {
  getSectionDisplayOrder,
  getSortedCmsOpportunitySections,
  getStrengthSectionTitle,
  SectionConfig,
  SortedCmsSection,
  StrengthSectionEntry,
} from './utils'

export { getSectionDisplayOrder, getSortedCmsOpportunitySections, type SortedCmsSection }

const resolveAccordionTitle = (
  key: CMSSectionKey,
  sectionData: CMSSectionBase & { sectionTitle?: string },
) => {
  const meta = CMS_SECTION_META[key]
  const fromCms = getCMSSectionTitle(key, sectionData)?.trim()
  if (fromCms) {
    return fromCms
  }
  if (meta?.defaultTitle?.trim()) {
    return meta.defaultTitle
  }
  return 'Details'
}

const getSectionIsOpen = (
  sectionKey: string,
  expandSection: boolean | undefined,
  openSections: Record<string, boolean>,
) => openSections[sectionKey] ?? expandSection === true

const CMSOpportunityDetails = ({
  opportunityDetails,
  project,
  onMissingDataClick,
  layout = 'stack',
}: {
  opportunityDetails: CMSOpportunityDetailsType
  project?: OpportunityDetails
  onMissingDataClick?: () => void
  layout?: 'stack' | 'expandable'
}) => {
  const sortedSections = useMemo(
    () => getSortedOpportunityDetailSanitySections(opportunityDetails),
    [opportunityDetails],
  )

  const [openSections, setOpenSections] = useState<Record<string, boolean>>({})

  const toggleSection = (key: string, expandSection?: boolean) => {
    setOpenSections((prev) => ({
      ...prev,
      [key]: !getSectionIsOpen(key, expandSection, prev),
    }))
  }

  const renderStandardSection = ({ key, component: Component, propKey }: SectionConfig) => {
    const sectionData = opportunityDetails[key] as CMSSectionBase & { sectionTitle?: string }
    const meta = CMS_SECTION_META[key]
    const props =
      key === 'additionalSections' && layout === 'expandable'
        ? { [propKey]: opportunityDetails[key], enableCollapsibleSections: true }
        : { [propKey]: opportunityDetails[key] }
    const sectionContent = <Component {...props} />

    if (layout === 'expandable') {
      if (meta?.usesOwnShell) {
        return <Component key={key} {...props} />
      }

      const title = resolveAccordionTitle(key, sectionData)
      const rowId = meta?.id ?? String(key)
      const isOpen = getSectionIsOpen(key, sectionData?.expandSection, openSections)

      return (
        <OpportunityExpandableSection
          key={key}
          id={`opportunity-expandable-${rowId}`}
          title={title}
          backgroundColor={resolveCmsSectionBackgroundHex(sectionData)}
          isOpen={isOpen}
          onToggle={() => toggleSection(key, sectionData?.expandSection)}
          sectionTestId={meta?.titleTestId}
        >
          {sectionContent}
        </OpportunityExpandableSection>
      )
    }

    if (meta?.usesOwnShell) {
      return <Component key={key} {...props} />
    }

    if (!meta) {
      return <Component key={key} {...props} />
    }

    return (
      <CMSSectionShell
        key={key}
        id={meta.id}
        title={getCMSSectionTitle(key, sectionData)}
        backgroundColor={resolveCmsSectionBackgroundHex(sectionData)}
        titleTestId={meta.titleTestId}
      >
        {sectionContent}
      </CMSSectionShell>
    )
  }

  const renderStrengthSection = (entry: StrengthSectionEntry) => {
    const sectionKey = entry.idSuffix
    const sectionContent = (
      <CMSStrengths strengths={entry.section} testIdPrefix={entry.testIdPrefix} />
    )

    if (layout === 'expandable') {
      const isOpen = getSectionIsOpen(sectionKey, entry.section.expandSection, openSections)

      return (
        <OpportunityExpandableSection
          key={sectionKey}
          id={`opportunity-expandable-${sectionKey}`}
          title={getStrengthSectionTitle(entry)}
          backgroundColor={resolveCmsSectionBackgroundHex(entry.section)}
          isOpen={isOpen}
          onToggle={() => toggleSection(sectionKey, entry.section.expandSection)}
          sectionTestId={entry.testIdPrefix}
        >
          {sectionContent}
        </OpportunityExpandableSection>
      )
    }

    return (
      <CMSSectionShell
        key={sectionKey}
        id={sectionKey}
        title={getStrengthSectionTitle(entry)}
        backgroundColor={resolveCmsSectionBackgroundHex(entry.section)}
        titleTestId={entry.testIdPrefix}
      >
        {sectionContent}
      </CMSSectionShell>
    )
  }

  const renderSection = (item: OpportunityDetailSanitySection) => {
    if (item.type === 'estimatedFinancials') {
      if (!project) {
        return null
      }

      return (
        <OpportunityEstimatedFinancials
          key='estimated-financials'
          project={project}
          opportunityDetails={opportunityDetails}
          onMissingDataClick={onMissingDataClick}
        />
      )
    }

    if (item.type === 'news') {
      return <OpportunityDetailNews key='news' opportunityDetails={opportunityDetails} />
    }

    if (item.type === 'leadership') {
      return (
        <OpportunityDetailLeadershipTeam key='leadership' opportunityDetails={opportunityDetails} />
      )
    }

    if (item.type === 'strength') {
      return renderStrengthSection(item.entry)
    }

    return renderStandardSection(item.config)
  }

  if (sortedSections.length === 0) {
    return null
  }

  if (layout === 'expandable') {
    return <>{sortedSections.map(renderSection)}</>
  }

  return <div className='opportunity-cms-sections-stack'>{sortedSections.map(renderSection)}</div>
}

export { CMSOpportunityDetails }
